import { useEffect, useRef } from 'react';
import { AIDropdownMinimal } from '@app/components/AIDropdown/AIDropdownMinimal';
import { VariableDropdown } from '@app/components/VariableDropdown/VariableDropdown';
import { 
  improveText, 
  changeTone,
  fixGrammarAndSpelling, 
  makeTextConcise, 
  regenerateText 
} from '@app/services/service';
import ChangeToneIcon from '@app/assets/icons/ai/change-tone.svg?react';
import ConciseIcon from '@app/assets/icons/ai/concise.svg?react';
import FormatIcon from '@app/assets/icons/ai/format.svg?react';
import GrammarIcon from '@app/assets/icons/ai/grammar.svg?react';
import RegenerateIcon from '@app/assets/icons/ai/regenerate.svg?react';
import Edit03Icon from '@app/assets/icons/edit-03.svg?react';
import sadCloudSvgUrl from '@app/assets/illustrations/sad-cloud.svg';
import emptyLogo from '@app/assets/images/empty-logo.png';

interface NegativeWindowContent {
  heading: string;
  description: string;
  imageUrl: string;
}

// Helper function to insert variable at cursor position
const insertVariableAtCursor = (
  currentValue: string,
  variable: string,
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>
): { newValue: string; cursorPosition: number } => {
  if (inputRef.current) {
    const input = inputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = currentValue.slice(0, start) + variable + currentValue.slice(end);
    const cursorPosition = start + variable.length;
    return { newValue, cursorPosition };
  }
  const newValue = currentValue + variable;
  return { newValue, cursorPosition: newValue.length };
};

const defaultNegativeWindowContent: NegativeWindowContent = {
  heading: 'We are very sorry that you had a bad experience!',
  description: 'We will do our best to correct the negative aspects you have identified. Thank you for being transparent with us!',
  imageUrl: sadCloudSvgUrl
};

interface NegativeWindowDesignEditorProps {
  content: NegativeWindowContent;
  onChange: (content: NegativeWindowContent) => void;
}

export const NegativeWindowDesignEditor = ({ content, onChange }: NegativeWindowDesignEditorProps) => {
  const headingRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);

  const handleHeadingVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(content.heading, variable, headingRef);
    onChange({ ...content, heading: newValue });
    setTimeout(() => {
      if (headingRef.current) {
        headingRef.current.setSelectionRange(cursorPosition, cursorPosition);
        headingRef.current.focus();
      }
    }, 0);
  };

  const handleDescriptionVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(content.description, variable, descriptionRef);
    onChange({ ...content, description: newValue });
    setTimeout(() => {
      if (descriptionRef.current) {
        descriptionRef.current.setSelectionRange(cursorPosition, cursorPosition);
        descriptionRef.current.focus();
      }
    }, 0);
  };

  // Load saved content when component mounts
  useEffect(() => {
    const storedData = localStorage.getItem('campaignFormData');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData.negativeWindowDesignContent) {
          // Merge with default values to ensure all new fields exist
          const savedContent = {
            ...defaultNegativeWindowContent,
            ...parsedData.negativeWindowDesignContent
          };
          // Explicitly preserve imageUrl from saved data if it exists (including empty string for defaults)
          if ('imageUrl' in parsedData.negativeWindowDesignContent) {
            savedContent.imageUrl = parsedData.negativeWindowDesignContent.imageUrl;
          }
          onChange(savedContent);
        }
      } catch (error) {
        // If parsing fails, keep default values
        console.error('Error parsing stored negative window data:', error);
      }
    }
  }, [onChange]);

  const handleAIAction = async (action: () => Promise<any>, field: 'heading' | 'description') => {
    const value = content[field];
    if (!value.trim()) return;
    
    try {
      const response = await action();
      if (response?.data?.text) {
        onChange({ ...content, [field]: response.data.text });
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  // Helper function to create tone options for Change tone dropdown
  const createToneOptions = (field: 'heading' | 'description') => {
    const value = content[field];
    const tones = [
      { label: 'Write appreciative', tone: 'appreciative' },
      { label: 'Write apologetic', tone: 'apologetic' },
      { label: 'Write informative', tone: 'informative' },
      { label: 'Write interactive', tone: 'interactive' },
      { label: 'Write humorous', tone: 'humorous' },
      { label: 'Write formal', tone: 'formal' },
      { label: 'Write casual', tone: 'casual' },
      { label: 'Write personalized', tone: 'personalized' }
    ];

    return tones.map(({ label, tone }) => ({
      label,
      Icon: <Edit03Icon className="w-4 h-4" />,
      onClick: () => handleAIAction(() => changeTone(value, tone), field)
    }));
  };

  const createAIActions = (field: 'heading' | 'description') => [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleAIAction(() => regenerateText(content[field]), field)
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(field)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleAIAction(() => fixGrammarAndSpelling(content[field]), field)
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleAIAction(() => makeTextConcise(content[field]), field)
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleAIAction(() => improveText(content[field]), field)
    }
  ];

  return (
    <div className="space-y-6">
      {/* Heading Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Heading</label>
        <div className="flex items-start gap-2">
          <input
            ref={headingRef}
            type="text"
            value={content.heading}
            onChange={(e) => onChange({ ...content, heading: e.target.value })}
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="We are very sorry that you had a bad experience!"
          />
          <div className="flex gap-1 items-center">
            <VariableDropdown onSelect={handleHeadingVariableSelect} />
            <div className="relative">
              <AIDropdownMinimal     
                actions={createAIActions('heading')}
                onlyIcon={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Description Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <div className="flex items-start gap-2">
          <input
            ref={descriptionRef}
            type="text"
            value={content.description}
            onChange={(e) => onChange({ ...content, description: e.target.value })}
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="We will do our best to correct the negative aspects you have identified. Thank you for being transparent with us!"
          />
          <div className="flex gap-1 items-center">
            <VariableDropdown onSelect={handleDescriptionVariableSelect} />
            <div className="relative">
              <AIDropdownMinimal     
                actions={createAIActions('description')}
                onlyIcon={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Image Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
        <div className="flex items-center gap-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg w-36 h-24 flex items-center justify-center overflow-hidden bg-white">
            {content.imageUrl && content.imageUrl.startsWith('data:') ? (
              <img src={content.imageUrl} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <img src={emptyLogo} alt="Empty logo" className="w-full h-full object-cover" />
            )}
          </div>
          <button 
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    onChange({ ...content, imageUrl: e.target?.result as string });
                  };
                  reader.readAsDataURL(file);
                }
              };
              input.click();
            }}
            className="px-4 py-2 bg-[#E7EAF4] text-gray-700 rounded-lg transition-colors"
          >
            Choose photo
          </button>
        </div>
      </div>
    </div>
  );
};
